import type { FieldPath, Fields } from '../../@types/field-path.d';
import type { AssetURLs, Attachment } from '../attachments';
import type { Comment } from '../comments';
import type { SeatType, User } from '../users';

export type CustomerStatus = 'active' | 'blocked' | 'deleted' | 'archived';
export type CustomerKind = 'user' | 'contact' | 'maintenance';

export type ContactGroup = {
	_id: string;
	name: string;
};

type Invite = {
	_id: string;
	invite_status: 'accepted' | 'pending' | 'expired';
	user_status: 'archived' | 'synced' | 'other_account' | 'active' | 'inactive' | null;
	invited: boolean;
	user: boolean;
};

export type Customer = {
	_id: string;
	name: string;
	email: string;
	cover: string | null;
	cover_url?: AssetURLs;
	status: CustomerStatus;
	kind: CustomerKind;
	user: User | null;
	role: string | null;
	barcodes: string[];
	rfidcodes: string[];
	fields: Record<string, string>;
	attachments?: Attachment[];
	comments?: Comment[];
	created: string;
	modified: string;
	archived: string | null;
	blocked: string | null;
	deleted: string | null;
	contactGroups: ContactGroup[];
	sync: string | null;
	workspaceAccess: boolean;
	lastLogin: string | null;
	seatType?: SeatType[];
	invite?: Invite;
};

export type CustomFieldKey = `fields.${string}`;
export type SystemField =
	| 'name'
	| 'created'
	| 'modified'
	| 'blocked'
	| 'seatType'
	| 'user.userSync.name'
	| 'lastLogin'
	| 'email'
	| 'access'
	| 'user.userRole.name'
	| 'contactGroups.name'
	| 'barcodes';

export type Field = SystemField | CustomFieldKey;

export type CustomerQueryField = FieldPath<Customer> | keyof Customer;

export type CustomerQueryData<F extends CustomerQueryField = CustomerQueryField> = Fields<Customer, F>;

export const CUSTOMER_SYSTEM_FIELDS = [
	{
		id: 'name',
		name: 'Name',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
	},
	{
		id: 'created',
		name: 'Created',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'modified',
		name: 'Modified',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'blocked',
		name: 'Blocked',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'seatType',
		name: 'Seat Type',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'user.userSync.name',
		name: 'User sync',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
	},
	{
		id: 'lastLogin',
		name: 'Last login',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'email',
		name: 'Email',
		kind: 'string',
		editor: 'email',
		unit: null,
		basicField: true,
	},
	{
		id: 'access',
		name: 'Access',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'user.userRole.name',
		name: 'Role',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'contactGroups.name',
		name: 'User groups',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
	},
	{
		id: 'barcodes',
		name: 'Barcode',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
] as const;
